import clsx from 'clsx';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { HiOutlineUpload } from 'react-icons/hi';

interface DropzoneProps {
  disabled: boolean;
  files: File[];
  setFiles: (files: File[]) => void;
  accept?: Record<string, string[]>;
  maxFiles?: number;
  maxSize?: number;
}

export const Dropzone = ({
  disabled,
  maxFiles,
  maxSize,
  accept,
  files,
  setFiles,
}: DropzoneProps) => {
  const [dragActive, setDragActive] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (disabled) return;

      const newFiles = maxFiles
        ? [...files, ...acceptedFiles].slice(0, maxFiles)
        : [...files, ...acceptedFiles];

      setFiles(newFiles);
      setDragActive(false);
    },
    [files, setFiles, disabled, maxFiles],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxFiles,
    maxSize,
    disabled,
    onDragEnter: () => setDragActive(true),
    onDragLeave: () => setDragActive(false),
  });

  const isActive = isDragActive || dragActive;

  return (
    <div
      {...getRootProps()}
      className={clsx(
        'flex flex-col items-center justify-center rounded-md border-2 border-dashed transition-colors cursor-pointer',
        'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
        'outline-none min-h-[120px] p-6',
        {
          'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100':
            !isActive && !disabled,
          'border-blue-500 bg-blue-50': isActive && !disabled,
          'border-gray-200 bg-gray-100 cursor-not-allowed opacity-50': disabled,
        },
      )}
    >
      <input {...getInputProps()} />

      <HiOutlineUpload
        className={clsx('h-8 w-8 mb-2', {
          'text-gray-400': !isActive && !disabled,
          'text-blue-500': isActive && !disabled,
          'text-gray-300': disabled,
        })}
      />

      <div className="text-center">
        <p
          className={clsx('text-sm', {
            'text-gray-600': !isActive && !disabled,
            'text-blue-600': isActive && !disabled,
            'text-gray-400': disabled,
          })}
        >
          Drag and drop files here
        </p>
        <p
          className={clsx('text-xs mt-1', {
            'text-gray-500': !isActive && !disabled,
            'text-blue-500': isActive && !disabled,
            'text-gray-400': disabled,
          })}
        >
          or click to browse!
        </p>
      </div>

      {maxFiles && (
        <p className="text-xs text-gray-500 mt-1">
          Maximum {maxFiles} file{maxFiles > 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
};
